import { EyeOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { Pagination, PRProcessTableRow } from '@app/models/pr_processes/PRProcessResponse';
import { LabServiceTableRow } from '@app/models/LabServiceConfig/LabServiceConfigResponse.api';
import { getLabServiceTableData } from '@app/api/labService.api';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 10,
};

export const LabServicesTable: React.FC = () => {
  const [tableData, setTableData] = useState<{ data: LabServiceTableRow[]; pagination: Pagination; loading: boolean }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });
  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[] | null>(null);
  const [deptOptions, setDeptOptions] = useState<{ label: string; value: string }[] | null>(null);
  const [typeOptions, setTypeOptions] = useState<{ label: string; value: string }[] | null>(null);

  const [form] = BaseButtonsForm.useForm();
  const lastFetchedRef = useRef(false);

  const fetch = useCallback(
    (params: any) => {
      setTableData((tableData) => ({ ...tableData, loading: true }));
      getLabServiceTableData(params)
        .then((res) => {
          if (isMounted.current) {
            setTableData({
              data: res.data.map((item: any) => ({ ...item, key: item.id })),
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              loading: false,
            });
            setStatusOptions(res.filters.statuses);
            setDeptOptions(
              Array.isArray(res.filters.departments)
                ? res.filters.departments.map((d) => ({ label: d.name, value: String(d.id) }))
                : [],
            );
            setTypeOptions(
              res.filters.types
                ? Object.entries(res.filters.types).map(([key, value]) => ({
                    label: value,
                    value: key,
                  }))
                : [],
            );
          }
        })
        .catch((error: any) => {
          notificationController.error({
            message: error.message,
          });
        });
    },
    [isMounted],
  );

  useEffect(() => {
    if (!lastFetchedRef.current) {
      lastFetchedRef.current = true;
      fetch(initialPagination);
    }
  }, [fetch]);

  const handleFilterChange = (newFilters: { [key: string]: string | [string, string] | null }) => {
    setFilters(newFilters);

    fetch({
      ...newFilters,
      current: 1,
      pageSize: tableData.pagination.pageSize,
    });
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      ...filters,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (record: LabServiceTableRow) => {
    navigate(`/view-lab-service`, { state: { id: record.id } });
  };

  const showTotal = (total: number, range: [number, number]): React.ReactNode => {
    return (
      <span
        style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}
      >{`Showing ${range[0]} - ${range[1]} of ${total} Lab Items`}</span>
    );
  };

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'department_id',
      placeholder: 'Departments',
      type: 'dropdown',
      options: deptOptions ? { deptOptions: deptOptions } : {},
    },
    {
      name: 'Status',
      placeholder: 'status',
      type: 'dropdown',
      options: statusOptions ? { statusOptions: statusOptions } : {},
    },
  ];

  const columns: ColumnsType<LabServiceTableRow> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Department',
      dataIndex: 'department_id',
      key: 'department_id',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '30%',
      render: (_, record) => {
        return (
          <Space>
            <Tooltip title="View">
              <Button
                type="text"
                onClick={() => handleViewRow(record)}
                icon={<EyeOutlined />}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <BaseCard id="basic-table" title="Lab Services" padding="1.25rem">
      <FilterComponent data={searchData} onFilter={handleFilterChange} />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true, scrollToFirstRowOnChange: true }}
      />
    </BaseCard>
  );
};
